import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, Plus, Search, Trash2 } from "lucide-react";
import DialogBox from "@/components/Common/DialogBox";
import Pagination from "@/components/Common/Pagination";
import useToastStore from "@/components/Common/Toast/toastStore";
import Loader from "@/components/Loader";
import usePaginationStore from "@/hooks/stores/usePaginationStore";
import { InterViewSlotService } from "@/services/InterViewSlot";

const InterviewSlotsUpload = () => {
  const navigate = useNavigate();
  const { showToast } = useToastStore;
  const [schedules, setSchedules] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [deletingKey, setDeletingKey] = useState(null);
  const [deleteConfirmRow, setDeleteConfirmRow] = useState(null);
  const [isDeletingSchedule, setIsDeletingSchedule] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const { getPerPage, setPerPage } = usePaginationStore();
  const [itemsPerPage, setItemsPerPage] = useState(() =>
    getPerPage("interview-slots-table"),
  );
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    fetchSchedules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const id = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 2000);

    return () => clearTimeout(id);
  }, [searchQuery]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchQuery, itemsPerPage]);

  const fetchSchedules = async () => {
    setIsLoading(true);
    setErrorMessage(null);
    try {
      const response = await InterViewSlotService.getSchedules();
      setSchedules(
        Array.isArray(response)
          ? response
          : response?.data && Array.isArray(response.data)
            ? response.data
            : [],
      );
    } catch (error) {
      const apiMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message;

      console.error("Error fetching schedules:", error);
      setErrorMessage(apiMessage || "Failed to load interview slots.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleView = (row) => {
    const vacancyId = row.vacancy_id;
    const statusId = row.vacancy_status_id;
    navigate(`/interviews-slots/${vacancyId}/${statusId}`);
  };

  const openDeleteConfirm = (row) => {
    const vacancyId = row.vacancy_id;
    const statusId = row.vacancy_status_id;
    if (!vacancyId || !statusId) return;
    setDeleteConfirmRow(row);
  };

  const closeDeleteConfirm = () => {
    if (isDeletingSchedule) return;
    setDeleteConfirmRow(null);
  };

  const confirmDeleteSlot = async () => {
    const row = deleteConfirmRow;
    if (!row) return;
    const vacancyId = row.vacancy_id;
    const statusId = row.vacancy_status_id;
    if (!vacancyId || !statusId) return;

    const key = `${vacancyId}-${statusId}`;
    setIsDeletingSchedule(true);
    setDeletingKey(key);
    try {
      await InterViewSlotService.deleteVacancyStatusBlocks(vacancyId, statusId);
      showToast({ message: "Schedule deleted successfully.", type: "success" });
      setDeleteConfirmRow(null);
      await fetchSchedules();
    } catch (error) {
      showToast({
        message: error.message || "Failed to delete schedule.",
        type: "error",
      });
    } finally {
      setIsDeletingSchedule(false);
      setDeletingKey(null);
    }
  };
  const displayedSchedules = useMemo(() => {
    const toTime = (d) => {
      if (!d) return 0;
      const t = new Date(d).getTime();
      return Number.isFinite(t) ? t : 0;
    };

    // Always show latest items at the top, deterministically.
    return [...schedules].sort((a, b) => {
      const createdDiff = toTime(b?.created_date) - toTime(a?.created_date);
      if (createdDiff !== 0) return createdDiff;

      const fromDiff = toTime(b?.from_date) - toTime(a?.from_date);
      if (fromDiff !== 0) return fromDiff;

      const statusDiff =
        (b?.vacancy_status_id ?? 0) - (a?.vacancy_status_id ?? 0);
      if (statusDiff !== 0) return statusDiff;

      return (b?.vacancy_id ?? 0) - (a?.vacancy_id ?? 0);
    });
  }, [schedules]);

  const filteredSchedules = useMemo(() => {
    const q = debouncedSearchQuery.trim().toLowerCase();
    if (!q) return displayedSchedules;

    const matches = (value) =>
      String(value ?? "")
        .toLowerCase()
        .includes(q);

    return displayedSchedules.filter((row) => {
      const companyName = row?.company?.name;
      const vacancyTitle = row?.vacancy?.title;
      const statusName = row?.vacancy_status?.status?.name;
      return (
        matches(companyName) || matches(vacancyTitle) || matches(statusName)
      );
    });
  }, [displayedSchedules, debouncedSearchQuery]);

  const totalPages = useMemo(() => {
    const per = Number(itemsPerPage) || 1;
    return Math.max(1, Math.ceil(filteredSchedules.length / per));
  }, [filteredSchedules.length, itemsPerPage]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const paginatedSchedules = useMemo(() => {
    const per = Number(itemsPerPage) || 1;
    const start = (currentPage - 1) * per;
    return filteredSchedules.slice(start, start + per);
  }, [filteredSchedules, currentPage, itemsPerPage]);

  const handlePerPageChange = (option) => {
    const next = option?.value ?? option;
    setItemsPerPage(next);
    setPerPage("interview-slots-table", next);
    setCurrentPage(1);
  };

  return (
    <div className="flex flex-col min-h-screen bg-background">
      <div className="flex-1 w-[90%] pt-12 px-8 mx-auto flex flex-col gap-8">
        {/* Header Section */}
        <div className="flex flex-col gap-4">
          <div className="flex justify-between items-start flex-wrap gap-4">
            <div className="flex flex-col gap-1">
              <h1 className="text-foreground text-2xl font-semibold font-['Inter'] leading-8">
                Interview Slots
              </h1>
              <p className="text-muted-foreground text-base font-normal font-['Inter'] leading-6">
                Manage your interview availability slots and recruitment
                schedules.
              </p>
            </div>
            <button
              onClick={() => navigate("/interviews-slots/create")}
              className="bg-primary-dark h-[36px] text-white px-4 py-2.5 rounded-lg shadow-sm font-semibold transition-all hover:bg-primary-dark flex items-center gap-2"
              id="create-slots-btn"
            >
              <Plus className="w-4 h-4" />
              Create Event Slots
            </button>
          </div>
          <div className="w-full md:w-[384px]">
            <div className="self-stretch h-11 px-3.5 py-2.5 bg-background rounded-lg border border-input inline-flex justify-start items-center gap-2 overflow-hidden">
              <Search className="w-5 h-5 text-muted-foreground" />
              <input
                id="slots-search-input"
                type="text"
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex-1 text-foreground placeholder:text-muted-foreground text-base font-normal leading-6 outline-none bg-transparent"
                autoComplete="off"
              />
            </div>
          </div>
        </div>

        <div className="bg-card shadow rounded-lg overflow-hidden border border-border">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-muted border-b border-border">
              <tr>
                <th className="px-6 py-5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Company Name
                </th>
                <th className="px-6 py-5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Vacancy Title
                </th>
                <th className="px-6 py-5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Status Name
                </th>
                <th className="px-6 py-5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Available Days
                </th>
                <th className="px-6 py-5 text-right text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-card divide-y divide-border">
              {isLoading ? (
                <tr>
                  <td
                    colSpan="5"
                    className="px-6 py-8 text-center text-muted-foreground"
                  >
                    <Loader bgColor="transparent" />
                  </td>
                </tr>
              ) : errorMessage ? (
                <tr>
                  <td
                    colSpan="5"
                    className="px-6 py-8 text-center text-red-600"
                  >
                    {errorMessage}
                  </td>
                </tr>
              ) : schedules.length === 0 ? (
                <tr>
                  <td
                    colSpan="5"
                    className="px-6 py-8 text-center text-muted-foreground"
                  >
                    No slots found
                    {debouncedSearchQuery.trim() ? " for your search." : "."}
                  </td>
                </tr>
              ) : (
                paginatedSchedules.map((row) => (
                  <tr
                    key={`${row?.vacancy_status_id ?? "status"}-${row?.vacancy_id ?? "vacancy"}-${row?.from_date ?? "from"}-${row?.to_date ?? "to"}-${row?.created_date ?? "created"}`}
                    className="hover:bg-muted transition-colors duration-200"
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-foreground">
                      {row.company?.name || "-"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                      {row.vacancy?.title || "-"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className="px-2 py-1 rounded-full text-xs font-semibold"
                        style={{
                          backgroundColor:
                            row.vacancy_status?.status?.bg_color + "20",
                          color: row.vacancy_status?.status?.bg_color || "#333",
                        }}
                      >
                        {row.vacancy_status?.status?.name || "-"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                      {row.blocks_count || 0}
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex justify-end items-center space-x-3">
                        <button
                          type="button"
                          className="text-muted-foreground hover:text-muted-foreground transition-colors inline-flex items-center gap-1"
                          onClick={() => handleView(row)}
                          title="View Details"
                        >
                          <Eye className="w-5 h-5" />
                        </button>
                        <button
                          type="button"
                          aria-label="Delete schedule"
                          disabled={
                            deletingKey ===
                            `${row.vacancy_id}-${row.vacancy_status_id}`
                          }
                          className="text-red-500 hover:text-red-700 disabled:opacity-50 transition-colors"
                          onClick={() => openDeleteConfirm(row)}
                          title="Delete"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <DialogBox
          isOpen={!!deleteConfirmRow}
          onClose={closeDeleteConfirm}
          title="Delete Interview Slot"
          className="w-[40%] max-md:w-[60%]"
          disableBackdropClick={isDeletingSchedule}
          actionButton={
            <button
              type="button"
              onClick={confirmDeleteSlot}
              disabled={isDeletingSchedule}
              className="inline-flex w-full justify-center items-center gap-2 rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-red-500 sm:ml-3 sm:w-auto disabled:opacity-50 disabled:pointer-events-none"
            >
              {isDeletingSchedule && (
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin shrink-0" />
              )}
              {isDeletingSchedule ? "Deleting…" : "Delete"}
            </button>
          }
        >
          <p className="text-muted-foreground text-sm mt-1 max-w-[390px]">
            Are you sure you want to delete this slot? This will remove all
            interview availability for{" "}
            {deleteConfirmRow ? (
              <>
                <span className="font-medium text-foreground">
                  {deleteConfirmRow.vacancy?.title || "this vacancy"}
                </span>
                {deleteConfirmRow.vacancy_status?.status?.name ? (
                  <> ({deleteConfirmRow.vacancy_status.status.name})</>
                ) : null}
              </>
            ) : null}
            . This action cannot be undone.
          </p>
        </DialogBox>
      </div>

      <div className="w-[90%] mx-auto p-6">
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          per_page={Number(itemsPerPage) || 1}
          onPerPageChange={handlePerPageChange}
        />
      </div>
    </div>
  );
};

export default InterviewSlotsUpload;
